import { useMemo } from 'react';
import { fetcher, fetcherWithMethod } from 'utils/axios';
import axios from 'utils/axios';
import { createGenericCRUD, useUploadFileData, invalidateCache } from './commonCrud';
import { CurrentTenant, Tenant, TenantBranding, TenantFilters } from 'types/tenant';
import useSWR, { mutate } from 'swr';
import { handleApiError } from 'utils/errorHandler';
import { CrudEndpoints } from 'types/common';

// Endpoints configuration
export const endpoints: CrudEndpoints = {
  key: 'Tenant',
  getList: '/GetList',
  getListById: '/GetList?tenantID={ID}',
  getModal: '/',
  getLookupList: '/GetLookupList',
  insert: '/',
  update: '/',
  delete: '',
  getlookupPost: true
};

// Create CRUD instance
const tenantCRUD = createGenericCRUD<Tenant>(endpoints);

export function useGetTenantsPicture(tenantID: number | null) {
  const response = useSWR<string>(tenantID !== null ? `Tenant/GetTenantLogoUrl/${tenantID}` : null, fetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    revalidateIfStale: false,
    dedupingInterval: 10000, // dedupe requests within 10 seconds
    onError: (err) => {
      handleApiError(err);
    }
  });
  return {
    ...response,
    data: response.data ?? null
  };
}

export function useGetTenantPictureThumbnail(tenantID: number | null) {
  const response = useSWR<string>(tenantID !== null ? `Tenant/GetTenantLogoThumbnailUrl/${tenantID}` : null, fetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    revalidateIfStale: false,
    dedupingInterval: 10000, // dedupe requests within 10 seconds
    onError: (err) => {
      handleApiError(err);
    }
  });
  return {
    ...response,
    data: response.data ?? null
  };
}

export async function useUpdateTenantPicture(tenantID: number, file: File | null): Promise<Tenant | null> {
  const result = await useUploadFileData<Tenant>(
    `Tenant/UpdateTenantLogo/${tenantID}`,
    null,
    {
      FileName: file
    },
    { showSnackbar: false, method: 'POST' }
  );

  if (result || file === null) {
    if (file === null) {
      // Optimistically clear cache to prevent 404s on deleted images
      await mutate(`Tenant/GetTenantLogoUrl/${tenantID}`, null, { revalidate: false });
      await mutate(`Tenant/GetTenantLogoThumbnailUrl/${tenantID}`, null, { revalidate: false });
    } else {
      await invalidateCache(`Tenant/GetTenantLogoUrl/${tenantID}`);
      await invalidateCache(`Tenant/GetTenantLogoThumbnailUrl/${tenantID}`);
    }
    await invalidateCache(`Tenant/UpdateTenantLogo`);
    // Force refresh of tenant list to pick up new updatedOn timestamp for cache busting
    await mutate((key) => typeof key === 'string' && key.startsWith('Tenant/GetList'), undefined, { revalidate: true });
  }
  return result;
}

export const useCheckDuplicateUniqueCode = async (uniqueCode: string, excludeID?: number): Promise<boolean> => {
  if (!uniqueCode) return false;
  const endpoint = `${endpoints.key}/CheckDuplicateUniqueCode?UniqueCode=${encodeURIComponent(uniqueCode)}${excludeID ? `&ExcludeID=${excludeID}` : ''}`

  try {
    const res = await fetcherWithMethod('GET', [endpoint, { method: 'GET' }]);
    return res as boolean;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export const useGetCurrentTenantDetail = () => {
  const { data, error, isLoading } = useSWR<CurrentTenant>(
    `${endpoints.key}/GetCurrentTenantDetail`,
    fetcher,
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      onError: handleApiError
    }
  );
  return {
    data,
    error,
    isLoading,
    mutate,
    hasError: !!error
  };

}

export function useGetTenantsWithFilters(filters: TenantFilters = {}, showAll: boolean = false) {
  const queryString = new URLSearchParams();

  Object.entries(filters as Record<string, any>).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      queryString.append(key, value.toString());
    }
  });
  queryString.set('showAll', showAll.toString());

  const fullEndpoint = queryString.toString()
    ? `${endpoints.key}${endpoints.getList}?${queryString.toString()}`
    : `${endpoints.key}${endpoints.getList}`;

  const { data, isLoading, error, isValidating, mutate } = useSWR(fullEndpoint, fetcher, {
    revalidateIfStale: true,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    onError: handleApiError
  });

  return useMemo(
    () => ({
      // Handle both new structure (data.tenants) and potential old structure/fallback (data) or empty
      data: data?.records || data?.tenants || data?.data || (Array.isArray(data) ? data : []),
      summary: data?.summary || null,
      totalRecordCount: data?.totalCount ?? data?.totalRecordCount,
      isLoading,
      error,
      mutate,
      isValidating,
      isEmpty: !isLoading && !(data?.tenants || data)?.length
    }),
    [data, error, isLoading, isValidating]
  );
}

export function useGetTenantListSummary() {
  const { data, isLoading, error, isValidating, mutate } = useSWR(
    `${endpoints.key}/GetListSummary`,
    fetcher,
    {
      revalidateIfStale: true,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      onError: handleApiError
    }
  );

  return useMemo(
    () => ({
      summary: data ?? null,
      isLoading,
      error,
      isValidating,
      mutate
    }),
    [data, error, isLoading, isValidating, mutate]
  );
}


export function useGetTenantLogHistory(tenantID: number | null, DateFrom: string | null = null, DateTo: string | null = null, UserID: number | null = null) {
  // Build query parameters
  const params = new URLSearchParams();
  if (tenantID) {
    params.append('TenantID', tenantID.toString());
  }
  if (DateFrom) {
    params.append('DateFrom', DateFrom);
  }
  if (DateTo) {
    params.append('DateTo', DateTo);
  }
  if (UserID !== null && UserID !== undefined) {
    params.append('UserID', UserID.toString());
  }

  const queryString = params.toString();
  const key = tenantID ? `${endpoints.key}/GetLog?${queryString}` : null;

  const { data, error, isLoading, mutate: mutateLog } = useSWR(
    key,
    fetcher,
    {
      revalidateIfStale: false,
      revalidateOnFocus: true,
      revalidateOnReconnect: false,
      onError: (err) => {
        handleApiError(err);
      }
    }
  );

  return {
    data: data ?? null,
    isLoading,
    error,
    mutateLog,
    refetch: () => mutate(key)
  };
}

export const useGetBranding = async (keyword: string): Promise<TenantBranding> => {
  try {
    // This is an open API call (no authentication required)
    const response = await axios.get(`/Auth/GetBranding/${keyword}`, {
      headers: {
        // Remove authorization header for this public endpoint
        Authorization: undefined
      }
    });
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      throw new Error('Tenant not found');
    }
    throw error;
  }
};

export const useUpdateActiveStatus = async (data: any) => {

  try {
    const response = await fetcherWithMethod('PUT', [`${endpoints.key}/UpdateActiveStatus`, { data }]);
    return response;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};


export const {
  useGetList: useGetTenantList,
  useGetListByID: useGetTenantListByID,
  useGetModal: useGetTenantModal,
  useInsertRecord: useInsertTenantRecord,
  useUpdateRecord: useUpdateTenantRecord,
  useDeleteRecord: useDeleteTenantRecord
} = tenantCRUD;
